function StakeholderPanel({
  actor,
  onClose,
  onEdit,
  alertas = [],
  signals = [],
  timeline = []
}) {
  if (!actor) return null;

  const relacional = actor._vita?.relacional || null;

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{actor.nombre}</h2>
            <p style={styles.subtitle}>
              {actor.tipo || 'Tipo no definido'} · {actor.comuna || 'Sin comuna'}
            </p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div style={styles.statusRow}>
          <Badge label="Estado" value={actor.estado} />
          <Badge label="Riesgo" value={actor.riesgo} />
          <Badge label="Influencia" value={actor.influencia} />
        </div>

        {/* SECCIÓN COGNITIVA VITA */}
        {actor._vita?.scoring && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Inteligencia territorial VITA</div>
            <div style={styles.scoringGrid}>
              <ScoreBox label="IVT" value={actor._vita.scoring.IVT} highlight />
              <ScoreBox label="IST" value={actor._vita.scoring.IST} />
              <ScoreBox label="IAT" value={actor._vita.scoring.IAT} />
              <ScoreBox label="IPN" value={actor._vita.scoring.IPN} />
            </div>
            {actor._vita?.state?.state && (
              <div style={styles.estadoEngine}>
                Estado engine:{' '}
                <strong style={{ color: '#0993b5' }}>
                  {actor._vita.state.state}
                </strong>
              </div>
            )}
          </div>
        )}

        {/* SECCIÓN RELACIONAL */}
        {relacional && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Motor relacional</div>

            <div style={styles.relacionalGrid}>
              <RelMetric
                label="Conexiones"
                value={relacional.grado}
              />
              <RelMetric
                label="Centralidad"
                value={`${relacional.centralidad}%`}
              />
              <RelMetric
                label="Nivel"
                value={relacional.nivelCentralidad}
              />
              <RelMetric
                label="Aliados"
                value={relacional.aliados}
              />
              <RelMetric
                label="Opositores"
                value={relacional.opositores}
              />
              <RelMetric
                label="Presión rel."
                value={relacional.presionRelacional}
              />
            </div>

            {relacional.esPuente && (
              <div style={styles.puenteBadge}>
                ⚡ Actor puente — conecta grupos desconectados entre sí
              </div>
            )}

            {relacional.actoresConectados.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <div style={styles.relacionLabel}>Actores conectados</div>
                {relacional.actoresConectados.map((c, i) => (
                  <div key={i} style={styles.conexionItem}>
                    <div style={styles.conexionNombre}>{c.nombre}</div>
                    <div style={styles.conexionMeta}>
                      <span style={{
                        ...styles.tipoBadge,
                        background:
                          c.tipo === 'Aliado' ? '#dcfae6' :
                          c.tipo === 'Opositor' ? '#fee4e2' : '#f0f2f5',
                        color:
                          c.tipo === 'Aliado' ? '#027a48' :
                          c.tipo === 'Opositor' ? '#b42318' : '#344054'
                      }}>
                        {c.tipo}
                      </span>
                      <span style={styles.intensidadText}>
                        {c.intensidad} · IVT {c.IVT}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {relacional.grado === 0 && (
              <p style={styles.aislado}>
                Sin conexiones declaradas. Actor aislado relacionalmente.
              </p>
            )}
          </div>
        )}

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Perfil territorial</div>
          <p style={styles.text}>
            {actor.observaciones || 'Sin observaciones registradas.'}
          </p>
          <div style={styles.infoGrid}>
            <Info label="Sector" value={actor.sector || '-'} />
            <Info label="Latitud" value={actor.latitud || '-'} />
            <Info label="Longitud" value={actor.longitud || '-'} />
            <Info label="Tags" value={actor.tags || '-'} />
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Actividad relacionada</div>
          <div style={styles.metrics}>
            <MiniMetric label="Alertas" value={alertas.length} />
            <MiniMetric label="RRSS" value={signals.length} />
            <MiniMetric label="Timeline" value={timeline.length} />
          </div>
        </div>

        <ActivitySection
          title="Alertas territoriales"
          empty="Sin alertas asociadas."
          items={alertas}
          render={(alerta) => (
            <>
              <strong>⚠️ {alerta.titulo}</strong>
              <br />
              Nivel: {alerta.nivel || '-'} · Estado: {alerta.estado || '-'}
            </>
          )}
        />

        <ActivitySection
          title="Señales sociales"
          empty="Sin señales sociales asociadas."
          items={signals}
          render={(signal) => (
            <>
              <strong>{signal.red || 'RRSS'}</strong> · {signal.sentimiento || '-'}
              <br />
              Riesgo: {signal.riesgo || '-'} · Ubicación: {signal.ubicacion || '-'}
            </>
          )}
        />

        <ActivitySection
          title="Timeline"
          empty="Sin eventos asociados."
          items={timeline}
          render={(evento) => (
            <>
              <strong>{evento.fecha || '-'}</strong>
              <br />
              {evento.titulo || '-'} · {evento.tipo || '-'}
            </>
          )}
        />

        <div style={styles.actions}>
          <button style={styles.editButton} onClick={() => onEdit(actor)}>
            Editar stakeholder
          </button>
        </div>
      </div>
    </div>
  );
}

function Badge({ label, value }) {
  return (
    <div style={styles.badge}>
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={styles.infoItem}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div style={styles.miniMetric}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ScoreBox({ label, value, highlight }) {
  return (
    <div style={{
      ...styles.scoreBox,
      background: highlight ? 'linear-gradient(135deg,#1fa97a,#0993b5)' : '#f8fafc',
      color: highlight ? 'white' : '#071a2f',
      border: highlight ? 'none' : '1px solid #e4e7ec'
    }}>
      <strong style={{ fontSize: '22px' }}>{value}</strong>
      <span style={{ fontSize: '11px', opacity: 0.8 }}>{label}</span>
    </div>
  );
}

function RelMetric({ label, value }) {
  return (
    <div style={styles.relMetric}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function ActivitySection({ title, items, render, empty }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionLabel}>{title}</div>
      {items.length === 0 && <p style={styles.empty}>{empty}</p>}
      {items.map((item) => (
        <div key={item.id} style={styles.activityItem}>
          {render(item)}
        </div>
      ))}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(7,26,47,0.35)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'flex-end'
  },
  panel: {
    width: '480px',
    height: '100%',
    background: 'white',
    padding: '30px',
    paddingBottom: '140px',
    overflowY: 'auto',
    boxSizing: 'border-box',
    boxShadow: '-12px 0 35px rgba(7,26,47,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '14px',
    marginBottom: '24px'
  },
  title: {
    margin: 0,
    color: '#071a2f',
    fontSize: '30px'
  },
  subtitle: {
    color: '#667085',
    marginTop: '6px'
  },
  closeButton: {
    background: '#f0f2f5',
    border: 'none',
    padding: '10px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    height: 'fit-content'
  },
  statusRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: '10px',
    marginBottom: '16px'
  },
  badge: {
    background: '#f8fafc',
    border: '1px solid #e4e7ec',
    borderRadius: '14px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    fontSize: '12px',
    color: '#667085'
  },
  section: {
    background: '#ffffff',
    border: '1px solid #e4e7ec',
    borderRadius: '18px',
    padding: '18px',
    marginBottom: '16px'
  },
  sectionLabel: {
    color: '#0993b5',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '14px'
  },
  scoringGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: '8px',
    marginBottom: '12px'
  },
  scoreBox: {
    borderRadius: '12px',
    padding: '12px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  estadoEngine: {
    fontSize: '13px',
    color: '#667085',
    marginTop: '4px'
  },
  relacionalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: '8px',
    marginBottom: '12px'
  },
  relMetric: {
    background: '#f8fafc',
    border: '1px solid #e4e7ec',
    borderRadius: '12px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#667085'
  },
  puenteBadge: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    color: '#92400e',
    borderRadius: '12px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 'bold',
    marginTop: '8px'
  },
  relacionLabel: {
    fontSize: '12px',
    color: '#667085',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  conexionItem: {
    background: '#f8fafc',
    border: '1px solid #e4e7ec',
    borderRadius: '12px',
    padding: '10px 14px',
    marginBottom: '6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  conexionNombre: {
    fontWeight: 'bold',
    color: '#071a2f',
    fontSize: '14px'
  },
  conexionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tipoBadge: {
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  intensidadText: {
    fontSize: '12px',
    color: '#667085'
  },
  aislado: {
    color: '#667085',
    fontSize: '13px',
    fontStyle: 'italic',
    marginTop: '8px'
  },
  text: {
    color: '#475467',
    lineHeight: '1.6'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2,1fr)',
    gap: '10px',
    marginTop: '14px'
  },
  infoItem: {
    background: '#f8fafc',
    padding: '11px',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '12px',
    color: '#667085'
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: '10px'
  },
  miniMetric: {
    background: '#eef4ff',
    color: '#092395',
    padding: '12px',
    borderRadius: '14px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  activityItem: {
    padding: '12px',
    borderRadius: '12px',
    background: '#f8fafc',
    marginBottom: '8px',
    color: '#344054',
    lineHeight: '1.5'
  },
  empty: {
    color: '#667085',
    fontSize: '14px'
  },
  actions: {
    marginTop: '22px'
  },
  editButton: {
    width: '100%',
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    border: 'none',
    padding: '14px',
    borderRadius: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }
};

export default StakeholderPanel;